/* ==========================================================================
   Lovedoll Senpai — Rapid Search: vendor line + hover image on result cards
   File: assets/lds-rapid-search.js
   Loaded from layout/theme.liquid, after lds-rapid-search.css
   --------------------------------------------------------------------------
   WHY THIS EXISTS

   Two things the theme's own product card does that Rapid Search's card does
   not, and that RS exposes no setting for. Both are put back here.

   1. THE VENDOR LINE. The theme's card (.sgn-product-card, defined inline in
      sections/collection-main.liquid) prints the vendor above the title as a
      small uppercase accent line. RS's card markup has no vendor field — the
      container carries only data-product-id. RS does hold the vendor data
      server-side, since it powers the Brand facet; it just never renders it.
      So the line is rebuilt by matching brand names RS already prints in its
      Brand sidebar against each card's title, which by the store's naming
      convention always begins with the brand.

   2. THE HOVER IMAGE. The theme's card ships two <img> elements and crossfades
      to the second one on hover. RS renders a single image and applies its own
      scale(1.06) zoom instead — which is what Chris noticed the day the search
      app changed over.

      RS's own search API already returns `secondaryImageUrl` for every product
      alongside `imageUrl`; it simply never renders it. So no extra network
      request is needed: window.fetch is wrapped, the response RS is already
      fetching is read as it goes past, and productId -> secondaryImageUrl is
      cached from it. A second <img> is then appended to each card and crossfaded
      by CSS exactly the way the theme's card does it.

   3. THE IMAGE BOX. The store's photography is 2:3 portrait. RS's card was
      being framed 3:4 with object-fit: cover, which cut roughly 11% off the
      bottom of every photo, and RS's own zoom scaled it further on hover.
      The box is set to 2:3 and the fit to contain, so the common case is a
      pixel-exact fit and an odd-shaped photo (a head, a torso) is shown whole
      with quiet bands rather than cropped.

   4. THE ENTER KEY. RS's search box only submits from its magnifier button;
      the Enter key does nothing, including the Go / Search key on a phone
      keyboard. Part 3 binds it to RS's own configured results page.

   Both data-driven features only ever act on data they are sure of. No brand
   match, no line; no secondary image, no second <img> — never a guess.
   ========================================================================== */
(function () {
  'use strict';

  function noop() {}

  /* ======================================================================
     PART 1 — VENDOR LINE
     ====================================================================== */

  /* Seed list — the store's vendors as of 2026-09.
     This is only a fallback for pages where neither the Brand facet nor the
     theme grid is in the DOM. A brand added later still works without
     touching this file: it will be picked up from the Brand facet the first
     time a shopper sees a page that lists it, and cached from then on. */
  var SEED = [
    'Climax Doll', 'Doll4Ever', 'Elsa Babe', 'Erovenus', 'Fanreal', 'Funwest',
    'Galatea', 'GameLady Doll', 'Irokebijin', 'Irontech', 'Jiusheng', 'Kospley',
    'MMX Doll', 'Piper Doll', 'Real Lady', 'Ridmii', 'SE Doll', 'Sigafun',
    'Sino Doll', 'SM Doll', 'Starpery', 'Top-Cydoll', 'TopFire', 'XT Doll',
    'Zelex'
  ];

  var CACHE_KEY = 'lds_rs_brands';
  var VENDOR_CLASS = 'rps-product-vendor';
  var DONE_ATTR = 'data-lds-vendor';

  /* ---- brand list ------------------------------------------------------ */

  function readCache() {
    try {
      var raw = window.sessionStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) { return []; }
  }

  function writeCache(list) {
    try { window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(list)); } catch (e) {}
  }

  function textOf(nodes) {
    return Array.prototype.map.call(nodes, function (n) {
      return (n.textContent || '').trim();
    }).filter(Boolean);
  }

  function collectBrands() {
    var found = []
      /* RS's own Brand facet. .rps-filter-manufacturer is a stable semantic
         class — the hashed module classes beside it are not, and are never
         targeted here. */
      .concat(textOf(document.querySelectorAll(
        '.rps-filter-manufacturer .rps-filter-checkbox-label')))
      /* The theme's server-rendered grid. Still in the DOM on collection
         pages (section 16 of the stylesheet only hides it), and these are
         the exact Shopify vendor strings. */
      .concat(textOf(document.querySelectorAll('.sgn-product-card__vendor')));

    var cached = readCache();
    var merged = cached.concat(SEED).concat(found);
    var seen = {};
    var out = [];
    for (var i = 0; i < merged.length; i++) {
      var v = merged[i];
      if (!v || v.length > 40) continue;
      var k = v.toLowerCase();
      if (seen[k]) continue;
      seen[k] = 1;
      out.push(v);
    }
    /* Only touch sessionStorage when the set actually grew — decorate() can
       run on every RS re-render and a write per render is pure waste. */
    if (out.length !== cached.length) writeCache(out);
    return out;
  }

  /* ---- matching -------------------------------------------------------- */

  function norm(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  /* Each brand yields one or two normalised forms to test as a title prefix:
     the whole name, and the name minus a trailing "doll". The store's titles
     are not consistent about that suffix — the vendor is "GameLady Doll" but
     titles read "Game Lady Full Silicone ...", while "Irontech" titles read
     "Irontech Doll ...". The short form is only used when it is still long
     enough to be unambiguous, which keeps "SE Doll" from matching on "se". */
  function formsFor(name) {
    var full = norm(name);
    if (!full) return [];
    var forms = [full];
    var short = full.replace(/doll$/, '');
    if (short.length >= 4 && short !== full) forms.push(short);
    return forms;
  }

  function buildIndex(brands) {
    var idx = [];
    for (var i = 0; i < brands.length; i++) {
      var forms = formsFor(brands[i]);
      for (var j = 0; j < forms.length; j++) {
        idx.push({ key: forms[j], label: brands[i] });
      }
    }
    /* Longest key first, so the most specific brand wins a tie. */
    idx.sort(function (a, b) { return b.key.length - a.key.length; });
    return idx;
  }

  /* All 78 in-stock listings are titled "USA IN STOCK Zelex SLE ...", i.e. the
     brand is not the first word. Strip a leading stock flag and test that form
     too, so those cards get a vendor line like every other card. The flag is
     only ever removed from the front, so it cannot affect a title that merely
     mentions stock later on. */
  var STOCK_PREFIX = /^(usa|us|eu|uk)?instock/;

  function brandForTitle(idx, title) {
    var n = norm(title);
    if (!n) return null;
    var candidates = [n];
    var stripped = n.replace(STOCK_PREFIX, '');
    if (stripped && stripped !== n) candidates.push(stripped);
    for (var c = 0; c < candidates.length; c++) {
      for (var i = 0; i < idx.length; i++) {
        if (candidates[c].indexOf(idx[i].key) === 0) return idx[i].label;
      }
    }
    return null;
  }

  /* ---- insertion ------------------------------------------------------- */

  function decorateVendors() {
    var titles = document.querySelectorAll(
      '.rps-product-container:not([' + DONE_ATTR + ']) .rps-product-title');
    if (!titles.length) return;

    var idx = buildIndex(collectBrands());
    if (!idx.length) return;

    for (var i = 0; i < titles.length; i++) {
      var title = titles[i];
      var card = title.closest('.rps-product-container');
      if (!card || card.getAttribute(DONE_ATTR)) continue;

      var label = brandForTitle(idx, title.getAttribute('title') || title.textContent);
      /* Mark either way: a card with no confident match should not be
         re-tested on every mutation. */
      card.setAttribute(DONE_ATTR, label ? '1' : '0');
      if (!label) continue;

      var el = document.createElement('p');
      el.className = VENDOR_CLASS;
      el.textContent = label;
      title.parentNode.insertBefore(el, title);
    }
  }

  /* ======================================================================
     PART 2 — HOVER IMAGE
     ====================================================================== */

  var HOVER_CLASS = 'lds-rs-hover-img';
  var IMG_DONE_ATTR = 'data-lds-hover';
  var STYLE_ID = 'lds-rs-hover-style';

  /* Only on devices that actually hover. A phone would download a second
     full-size photo per card and never show one of them. */
  var CAN_HOVER = !!(window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches);

  /* productId (string) -> secondary image URL */
  var SECOND = {};
  /* RS search API URLs already re-read by backfill(), so it runs at most once
     per distinct grid render rather than on every mutation. */
  var backfilled = {};

  /* These rules live here, not in assets/lds-rapid-search.css, because they
     describe elements and states this file creates and manages. Keeping them
     together means they cannot drift apart.

     NOTE for whoever next opens assets/lds-rapid-search.css: section 10 of that
     file still sets `aspect-ratio: 3 / 4` and `object-fit: cover` on
     .rps-product-image-container / img.rps-product-image. Those two
     declarations are superseded here and are safe to delete next time that file
     is edited for another reason — not worth a 51 KB round-trip on their own.

     Injected once at start(), before RS has rendered its grid, so the cards are
     never painted with the old framing first. */
  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var css =
      /* The photography is 2:3. Framing the box the same way means the common
         case fits exactly, and `contain` guarantees nothing is ever cropped or
         scaled up — an odd-shaped photo gets bands on the dark card instead. */
      '.rps-product-container .rps-product-image-container' +
      '{aspect-ratio:2/3!important}' +
      '.rps-product-container img.rps-product-image,' +
      '.rps-product-container .' + HOVER_CLASS +
      '{object-fit:contain!important;object-position:center!important}' +
      /* RS ships an inline rule that zooms the photo on hover. The theme's own
         card does not scale, it crossfades. */
      '.rps-product-container:hover img.rps-product-image,' +
      '.rps-product-container:hover .' + HOVER_CLASS +
      '{transform:none!important}' +
      /* The second photo, stacked and crossfaded exactly like the theme's
         .sgn-product-card__hover-img. */
      '.' + HOVER_CLASS + '{position:absolute;inset:0;width:100%;height:100%;' +
      'display:block;opacity:0;z-index:1;pointer-events:none;' +
      'transition:opacity .4s ease}' +
      'img.rps-product-image{transition:opacity .4s ease!important}' +
      '.rps-product-container:hover .' + HOVER_CLASS + '{opacity:1}' +
      /* Scoped to cards that actually HAVE a second photo. Unscoped, a
         single-photo card would fade to an empty box on hover. */
      '.rps-product-container[' + IMG_DONE_ATTR + '="1"]:hover ' +
      'img.rps-product-image{opacity:0}';
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = css;
    (document.head || document.documentElement).appendChild(el);
  }

  function isRsSearchUrl(url) {
    return typeof url === 'string' &&
      url.indexOf('rapid-product-search') !== -1 &&
      url.indexOf('/search/') !== -1;
  }

  /* Reads productId -> secondaryImageUrl out of an RS search response.
     Returns true when something new was learned, so the caller can redraw. */
  function absorb(json) {
    var list = json && json.products;
    if (!list || !list.length) return false;
    var added = false;
    for (var i = 0; i < list.length; i++) {
      var p = list[i];
      if (!p || !p.productId) continue;
      var src = p.secondaryImageUrl;
      /* RS repeats the primary image when a product has only one photo. */
      if (!src || src === p.imageUrl) continue;
      var key = String(p.productId);
      if (!SECOND[key]) { SECOND[key] = src; added = true; }
    }
    return added;
  }

  /* Wrap fetch rather than issuing our own request: RS is already asking for
     exactly this data on every render, filter, sort and page change. The
     wrapper is fully transparent — it returns RS's own promise untouched and
     reads a clone. Installed at parse time so it is in place before RS mounts. */
  function watchFetch() {
    if (!window.fetch || window.fetch.__ldsRsWrapped) return;
    var original = window.fetch;
    var wrapped = function (input, init) {
      var promise = original.apply(this, arguments);
      try {
        var url = typeof input === 'string' ? input : (input && input.url);
        if (isRsSearchUrl(url)) {
          promise.then(function (response) {
            try {
              response.clone().json().then(function (json) {
                if (absorb(json)) schedule();
              }, noop);
            } catch (e) {}
          }, noop);
        }
      } catch (e) {}
      return promise;
    };
    wrapped.__ldsRsWrapped = true;
    try { window.fetch = wrapped; } catch (e) {}
  }

  /* Safety net for the case where RS got its response in before this file ran.
     Re-reads the most recent RS search URL off the performance timeline. Costs
     one request, only when a card is on screen whose id we do not have. */
  function backfill() {
    var url = null;
    try {
      var entries = window.performance.getEntriesByType('resource');
      for (var i = entries.length - 1; i >= 0; i--) {
        if (isRsSearchUrl(entries[i].name)) { url = entries[i].name; break; }
      }
    } catch (e) { return; }
    if (!url || backfilled[url]) return;
    backfilled[url] = true;
    try {
      window.fetch(url)
        .then(function (r) { return r.json(); })
        .then(function (json) { if (absorb(json)) schedule(); }, noop);
    } catch (e) {}
  }

  function decorateImages() {
    if (!CAN_HOVER) return;
    var cards = document.querySelectorAll(
      '.rps-product-container:not([' + IMG_DONE_ATTR + '])');
    if (!cards.length) return;

    var misses = 0;
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var id = card.getAttribute('data-product-id');
      var box = card.querySelector('.rps-product-image-container');
      if (!id || !box || !box.querySelector('img.rps-product-image')) continue;

      var src = SECOND[String(id)];
      /* Deliberately left unmarked so it is retried once backfill lands. */
      if (!src) { misses++; continue; }

      card.setAttribute(IMG_DONE_ATTR, '1');
      if (box.querySelector('.' + HOVER_CLASS)) continue;

      var img = document.createElement('img');
      img.className = HOVER_CLASS;
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      img.setAttribute('aria-hidden', 'true');
      box.appendChild(img);
    }

    if (misses) backfill();
  }

  /* ======================================================================
     PART 3 — THE ENTER KEY

     Rapid Search's own search box has no keyboard path to its results page.
     Type a query, press Enter, and nothing happens at all: only the magnifier
     button submits. On a phone that key is the keyboard's Go / Search button,
     which is how most people finish a search, so the query is simply lost —
     by far the most expensive of the gaps this file closes.

     Deliberately NOT bound to an RS class name. RS ships hashed CSS-module
     classes that change with every app release, so the listener asks a
     structural question instead: is the focused input inside a Rapid Search
     widget or a search landmark, and is it the query box rather than one of
     the sidebar's facet filters. That survives RS updates, and it cannot
     reach a newsletter, quantity, address or filter field.

     Bound on window in the capture phase — the earliest point in the event's
     path — because RS's own handler swallows the key, and a bubble-phase or
     document-phase listener would be reading an event that had already been
     dealt with. For the same reason e.defaultPrevented is deliberately NOT
     treated as a reason to stand down: RS preventing the default IS the bug.
     ====================================================================== */

  /* Fallback only. RS publishes the page it is actually configured to use,
     and resultsPath() prefers that, so the two can never drift apart. */
  var RESULTS_PATH = '/pages/rapid-search-results';

  function resultsPath() {
    try {
      var s = window.RapidSearchSettings;
      var u = s && (s.resultpage_url || s.resultPageUrl || s.result_page_url);
      if (u && typeof u === 'string') return '/' + u.replace(/^\/+/, '');
    } catch (e) {}
    return RESULTS_PATH;
  }

  /* Carry Shopify's preview parameters across the jump. A shopper never has
     them; anyone testing search on an unpublished theme would otherwise be
     dropped back onto the live theme the moment they pressed Enter. */
  function previewQuery() {
    var out = [];
    try {
      var sp = new URLSearchParams(window.location.search);
      sp.forEach(function (v, k) {
        if (k === 'preview_theme_id' || k.charAt(0) === '_') {
          out.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
        }
      });
    } catch (e) {}
    return out;
  }

  /* Add ?lds_debug=1 to any URL and this narrates what it saw when Enter was
     pressed; silent otherwise. Left in deliberately. The markup underneath is
     an app's, it changes with every RS release without warning, and this is
     the difference between diagnosing a future "search does nothing" report
     in one page load and rediscovering the whole DOM from scratch. */
  var DEBUG = false;
  try {
    DEBUG = window.location.search.indexOf('lds_debug=1') !== -1;
  } catch (e) {}

  function debug(msg, detail) {
    if (!DEBUG) return;
    try { window.console.log('[LDS enter] ' + msg, detail); } catch (e) {}
  }

  /* Both of RS's naming conventions: rps_underscore on the dropdown,
     rps-hyphen on the result grid. */
  var RS_CLASS = /^rps[-_]/;
  var FACET_CLASS = /filter|facet|refine/i;

  /* A <search> element carries the search role implicitly and has no role
     attribute, so both spellings have to be tested. */
  function isSearchLandmark(node) {
    if (node.tagName === 'SEARCH') return true;
    return !!(node.getAttribute && node.getAttribute('role') === 'search');
  }

  /* parentNode is null at a shadow root; stepping through its host keeps the
     walk going, so a box the app renders inside a shadow tree is still seen. */
  function parentOf(node) {
    return node.parentNode || node.host || null;
  }

  /* True only for the query box of a search widget. Walks up a bounded number
     of ancestors — far enough to clear the app's own wrapper divs, not so far
     that <body> makes every input on the page look like a search box. */
  function isRsQueryInput(el) {
    var node = el;
    var rs = false;
    for (var n = 0; n < 10 && node && node !== document.body; n++) {
      if (node.nodeType === 1) {
        var cls = typeof node.className === 'string' ? node.className : '';
        var parts = cls.split(/\s+/);
        for (var i = 0; i < parts.length; i++) {
          if (!parts[i]) continue;
          /* One facet-ish class anywhere in the chain disqualifies it
             outright: a sidebar's "search brands" box must keep filtering
             the list, not navigate the shopper off the page. */
          if (FACET_CLASS.test(parts[i])) return false;
          if (RS_CLASS.test(parts[i])) rs = true;
        }
        /* The theme's header carries no search form of its own — only a link
           to /search — so a search landmark on the page belongs to RS. */
        if (!rs && isSearchLandmark(node)) rs = true;
      }
      node = parentOf(node);
    }
    return rs;
  }

  /* Debug only. */
  function chainOf(el) {
    var out = [];
    var node = el;
    for (var n = 0; n < 10 && node && node !== document.body; n++) {
      if (node.nodeType === 1) {
        var cls = typeof node.className === 'string' ? node.className : '';
        out.push(node.tagName.toLowerCase() +
          (cls ? '.' + cls.trim().replace(/\s+/g, '.') : '') +
          (node.getAttribute('role') ? '[role=' + node.getAttribute('role') + ']' : ''));
      } else {
        out.push('#' + (node.nodeName || 'node'));
      }
      node = parentOf(node);
    }
    return out.join('  <  ');
  }

  function onEnter(e) {
    if (e.key !== 'Enter') return;
    /* An IME uses Enter to commit the candidate word, not to submit. Japanese
       and Chinese shoppers are a real share of this store's traffic. */
    if (e.isComposing || e.keyCode === 229) return;

    /* composedPath()[0], not target: an event that crosses a shadow boundary
       is retargeted to the host element on the way out, and the input itself
       would never be seen. */
    var el = e.target;
    try {
      if (typeof e.composedPath === 'function') {
        var path = e.composedPath();
        if (path && path.length) el = path[0];
      }
    } catch (err) {}

    if (!el || el.tagName !== 'INPUT') {
      debug('not an input', el && el.tagName);
      return;
    }
    var type = (el.type || 'text').toLowerCase();
    if (type !== 'search' && type !== 'text') {
      debug('wrong input type', type);
      return;
    }

    var match = isRsQueryInput(el);
    debug(match ? 'match' : 'no match', chainOf(el));
    if (!match) return;

    var q = (el.value || '').trim();
    /* Empty box: let RS do whatever it does. Never navigate to a blank
       result page on a stray keypress. */
    if (!q) return;

    e.preventDefault();
    e.stopPropagation();
    var qs = ['q=' + encodeURIComponent(q)].concat(previewQuery());
    window.location.assign(resultsPath() + '?' + qs.join('&'));
  }

  /* ======================================================================
     LIFECYCLE
     ====================================================================== */

  /* Category facet: hide the catch-all collections. Every product is in
     "All", "Products", "New products" and "Dolls", and "Dolls By Style" is a
     menu-only parent, so as filters they narrow nothing.
     Only the facet rows are hidden; the collections themselves, their pages
     and the Brand facet are untouched. Matched on the label text because RS
     gives the rows no value or id attribute. (2026-11-03) */
  var HIDDEN_CATEGORIES = {
    'all': 1, 'products': 1, 'new products': 1, 'dolls': 1, 'dolls by style': 1
  };
  function hideCatchAllCategories() {
    var labels = document.querySelectorAll(
      '.rps-filter-block-body-category_id .rps-filter-checkbox-label');
    for (var i = 0; i < labels.length; i++) {
      var name = (labels[i].textContent || '').trim().toLowerCase();
      if (!HIDDEN_CATEGORIES[name]) continue;
      var row = labels[i].closest('label.rps-filter-checkbox') || labels[i];
      if (row.style.display !== 'none') row.style.display = 'none';
    }
  }

  /* Tag-based facets show the raw tag suffix ("a", "in-stock", "s-tpe").
     Rewrite the visible label only: the checkbox value RS filters on is
     untouched. The text node's nodeValue is edited in place (never replaced)
     so React keeps its own reference to it; if RS re-renders a raw value the
     characterData observer below puts the pretty one back. Height and Weight
     are already readable and are left alone. (2026-11-03) */
  var PRETTY_FACETS = {
    'availability': 1, 'doll type': 1, 'cup size': 1, 'material': 1,
    'skin tone': 1, 'features': 1, 'series': 1, 'body type': 1
  };
  var PRETTY_WHOLE = {
    's-tpe': 'S-TPE', 'k-series': 'K-Series', 'uniseries': 'UniSeries',
    'in-stock': 'In Stock', 'full-doll': 'Full Doll', 'evo-max-2': 'EVO Max 2'
  };
  var PRETTY_UPPER = {
    'tpe': 1, 'sle': 1, 'ros': 1, 'evo': 1, '3d': 1, 'ido': 1, 'md': 1,
    'se': 1, 'ulw': 1, 'bbw': 1
  };
  function prettyWord(w) {
    if (PRETTY_UPPER[w] || /^\d+[a-z]$/.test(w) || /^id\d/.test(w)) {
      return w.toUpperCase();
    }
    if (w === 'instock') return 'In Stock';
    return w.charAt(0).toUpperCase() + w.slice(1);
  }
  function prettyLabel(facet, raw) {
    var v = raw.toLowerCase();
    if (facet === 'cup size') {
      return /^[a-z]{1,2}$/.test(v) ? v.toUpperCase() + ' Cup' : raw;
    }
    if (PRETTY_WHOLE[v]) return PRETTY_WHOLE[v];
    if (!/^[a-z0-9-]+$/.test(v)) return raw;   /* already a display string */
    return v.split('-').map(prettyWord).join(' ');
  }
  function facetName(body) {
    var p = body;
    for (var i = 0; i < 4 && p; i++) {
      p = p.parentElement;
      var h = p && p.querySelector('.rps-filter-block-header');
      if (h && !body.contains(h)) return (h.textContent || '').trim().toLowerCase();
    }
    return '';
  }
  function prettifyFacetLabels() {
    var bodies = document.querySelectorAll('.rps-filter-block-body-tags');
    for (var i = 0; i < bodies.length; i++) {
      var facet = facetName(bodies[i]);
      if (!PRETTY_FACETS[facet]) continue;
      var spans = bodies[i].querySelectorAll('.rps-filter-checkbox-label');
      for (var j = 0; j < spans.length; j++) {
        var node = spans[j].firstChild;
        if (!node || node.nodeType !== 3) continue;
        var raw = node.nodeValue.trim();
        var nice = prettyLabel(facet, raw);
        if (nice !== raw) node.nodeValue = nice;
      }
    }
  }

  /* setTimeout rather than requestAnimationFrame: rAF is parked while a tab
     is in the background, and RS often finishes rendering in a tab the
     shopper opened but has not looked at yet. */
  var pending = null;
  function schedule() {
    if (pending) return;
    pending = window.setTimeout(function () {
      pending = null;
      decorateVendors();
      decorateImages();
      hideCatchAllCategories();
      prettifyFacetLabels();
    }, 16);
  }

  function start() {
    /* Unconditional: the framing fix applies on every device, while the second
       <img> below is desktop-only. */
    injectStyle();
    schedule();
    /* RS re-renders the whole grid on filter, sort and pagination, so watch
       the subtree rather than hooking any one of its events. */
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  watchFetch();
  window.addEventListener('keydown', onEnter, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}());
